class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('CartDrawer');
    this.overlay = document.getElementById('CartDrawerOverlay');
    this.panel = document.getElementById('CartDrawerPanel');
    this.closeBtn = document.getElementById('CartDrawerClose');
    
    // Header trigger and bubble
    this.headerTrigger = document.getElementById('CartDrawerTrigger');
    this.headerBubble = document.getElementById('CartCountBubble');

    this.isOpen = false;
    this.isUpdating = false;

    this.bindEvents();
  }

  bindEvents() {
    if (this.headerTrigger) {
      this.headerTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Bind quantity and remove buttons inside the drawer dynamically
    this.drawer.addEventListener('click', (e) => {
      // Remove button
      const removeBtn = e.target.closest('.js-remove-item');
      if (removeBtn) {
        e.preventDefault();
        this.updateItem(removeBtn.dataset.key, 0);
        return;
      }

      // Qty plus/minus buttons
      const qtyBtn = e.target.closest('.js-qty-btn');
      if (qtyBtn) {
        e.preventDefault();
        const action = qtyBtn.dataset.action;
        const key = qtyBtn.dataset.key;
        const input = this.drawer.querySelector(`.js-qty-input[data-key="${key}"]`);
        
        if (input) {
          let value = parseInt(input.value) || 0;
          if (action === 'plus') {
            value += 1;
          } else if (action === 'minus') {
            value = Math.max(0, value - 1);
          }
          this.updateItem(key, value);
        }
      }
    });

    // Qty input change
    this.drawer.addEventListener('change', (e) => {
      if (e.target.classList.contains('js-qty-input')) {
        const key = e.target.dataset.key;
        const value = parseInt(e.target.value) || 0;
        this.updateItem(key, value);
      }
    });

    // Intercept generic Add To Cart forms
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.matches('form[action="/cart/add"], form[action="/cart/add.js"]')) {
        e.preventDefault();
        this.addItem(new FormData(form), form);
      }
    });
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    
    this.drawer.classList.remove('invisible');
    
    // Slight delay for animations to kick in
    requestAnimationFrame(() => {
      this.overlay.classList.remove('opacity-0');
      this.panel.classList.remove('translate-x-full');
      if (typeof lenis !== 'undefined') lenis.stop();
      document.body.style.overflow = 'hidden';
    });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    
    this.overlay.classList.add('opacity-0');
    this.panel.classList.add('translate-x-full');
    
    // Wait for transition before hiding completely
    setTimeout(() => {
      this.drawer.classList.add('invisible');
      if (typeof lenis !== 'undefined') lenis.start();
      document.body.style.overflow = '';
    }, 300); // 300ms matches Tailwind's duration-300
  }

  async addItem(formData, form = null) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    
    let submitBtn = null;
    let originalContent = '';
    
    if (form) {
      submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        originalContent = Array.from(submitBtn.childNodes).map(n => n.cloneNode(true));
        submitBtn.style.minWidth = submitBtn.offsetWidth + 'px';
        const spinner = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        spinner.setAttribute('class', 'h-5 w-5 animate-spin mx-auto');
        spinner.setAttribute('fill', 'none');
        spinner.setAttribute('viewBox', '0 0 24 24');
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('class', 'opacity-25');
        circle.setAttribute('cx', '12'); circle.setAttribute('cy', '12'); circle.setAttribute('r', '10');
        circle.setAttribute('stroke', 'currentColor'); circle.setAttribute('stroke-width', '4');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('class', 'opacity-75');
        path.setAttribute('fill', 'currentColor');
        path.setAttribute('d', 'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z');
        spinner.append(circle, path);
        submitBtn.replaceChildren(spinner);
      }
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        alert(error.description || 'Error adding item to cart');
        this.isUpdating = false;
        return;
      }
      
      await this.refreshDrawer();
      this.open();
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      this.isUpdating = false;
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.replaceChildren(...originalContent);
        submitBtn.style.minWidth = '';
      }
    }
  }

  async updateItem(key, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: ['cart-drawer'] // Request our section back
        })
      });
      
      const state = await response.json();
      
      // Update HTML from returned section data
      this.renderContents(state.sections['cart-drawer']);
      this.updateHeaderCount(state.item_count);
      
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  async refreshDrawer() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
      const text = await response.text();
      this.renderContents(text);
      
      // Also fetch cart.js to update the bubble count
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cartState = await cartResponse.json();
      this.updateHeaderCount(cartState.item_count);
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  renderContents(html) {
    // Parse the returned HTML to extract just the section content
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newContents = doc.getElementById('CartDrawer');
    
    if (newContents) {
      // Keep the wrapper, replace the inside (panel & overlay are structurally the same)
      const currentDrawer = document.getElementById('CartDrawer');
      
      // Copy over state classes so it doesn't flicker close
      const isPanelOpen = !this.panel.classList.contains('translate-x-full');
      
      currentDrawer.replaceChildren(...Array.from(newContents.childNodes).map(n => n.cloneNode(true)));
      
      // Re-assign references since DOM changed
      this.overlay = document.getElementById('CartDrawerOverlay');
      this.panel = document.getElementById('CartDrawerPanel');
      this.closeBtn = document.getElementById('CartDrawerClose');
      
      if (isPanelOpen) {
         this.overlay.classList.remove('opacity-0');
         this.panel.classList.remove('translate-x-full');
      } else {
         this.overlay.classList.add('opacity-0');
         this.panel.classList.add('translate-x-full');
      }
      
      this.bindEvents(); // Re-bind since elements are new
    }
  }

  updateHeaderCount(count) {
    if (this.headerBubble) {
      this.headerBubble.textContent = count;
      if (count > 0) {
        this.headerBubble.classList.remove('hidden');
        this.headerBubble.classList.add('flex');
      } else {
        this.headerBubble.classList.add('hidden');
        this.headerBubble.classList.remove('flex');
      }
    }
  }
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});
